/**
 * Граница ошибки: один сломавшийся блок не должен уносить весь продукт.
 *
 * React не даёт компоненту пережить исключение потомка: без границы одно
 * неожиданное `undefined` из ответа сервера — например, поле, которое контракт
 * объявляет обязательным, а работающий рядом бэкенд ещё не отдаёт, — оставляет
 * белый экран вместо приложения. Экраны здесь щепетильны к честной деградации
 * («данных нет» ≠ «ноль»), и полностью белая страница была единственным местом,
 * где отказ выглядел как отсутствие продукта, а не как отсутствие данных.
 *
 * Граница говорит тем же словарём, что и `DataStatePanel state="error"`, и
 * даёт две двери наружу: повторить отрисовку и уйти на уровень района.
 * Сбрасывается сменой `resetKey` (обычно адрес): иначе один упавший экран
 * оставался бы упавшим до перезагрузки страницы.
 */

import { Component, type ErrorInfo, type ReactNode } from "react";
import { defineMessages } from "../i18n/define";
import { COMMON } from "../i18n/common";
import { tr } from "../i18n/runtime";
import { Button, DataStatePanel } from "./ui";

interface Props {
  children: ReactNode;
  /** Смена значения возвращает границу в рабочее состояние. */
  resetKey?: string;
  /** Что сломалось — словами читателя, а не именем компонента. */
  title?: string;
}

interface State {
  error: Error | null;
}

const m = defineMessages({
  title: { id: "common.kit.errorBoundary.title", defaultMessage: "Экран не отрисовался", description: "Заголовок панели ошибки, когда экран упал при отрисовке (старая оболочка)" },
  body: {
    id: "common.kit.errorBoundary.body",
    defaultMessage:
      "Ответ сервера не сходится с тем, что экран умеет показать. Это отказ отрисовки, а не утверждение о данных района: ничего из показанного рядом не следует считать посчитанным. Техническая причина: {reason}",
    description: "Текст панели ошибки отрисовки; {reason} — техническое сообщение ошибки (не переводится)",
  },
});

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidUpdate(previous: Props) {
    if (previous.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null });
    }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Сборщика ошибок в пилоте нет; консоль — единственный адрес, и молчать
    // тут нельзя: причина отказа обязана остаться доступной.
    // i18n-ignore: сообщение для разработчика в консоли
    console.error("Экран упал:", error, info.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    return (
      <DataStatePanel
        state="error"
        title={this.props.title ?? tr(m.title)}
        action={
          <Button onClick={() => this.setState({ error: null })}>
            {tr(COMMON.retry)}
          </Button>
        }
      >
        {tr(m.body, { reason: error.message })}
      </DataStatePanel>
    );
  }
}
